import { saveAs } from 'file-saver';
import ExcelJS from 'exceljs';
import { toastWarn } from '../Toast/ToastComponent';
import Chart from 'chart.js/auto';

// ─── Chart Image Generator ───────────────────────────────────────────────────
const generateChartImage = async (
  labels, dataValues, chartTitle,
  chartType = 'pie', stackedData = null,
  canvasWidth = 500, canvasHeight = 320
) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    canvas.style.position = 'absolute';
    canvas.style.left = '-9999px';
    document.body.appendChild(canvas);

    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const COLORS = [
      '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
      '#FF9F40', '#E7E9ED', '#B22222', '#228B22', '#0000CD'
    ];

    // Clean axis config — no box borders
    const cleanScaleX = {
      stacked: false,
      border: { display: false },
      grid: { display: false },
      ticks: { color: '#444', font: { size: 11, family: 'Calibri' } },
    };
    const cleanScaleY = (stacked = false) => ({
      stacked,
      beginAtZero: true,
      border: { display: false },
      grid: { color: '#ececec', drawBorder: false },
      ticks: { color: '#444', font: { size: 11, family: 'Calibri' } },
    });

    let chartConfig;

    if (chartType === 'stackedBar' && stackedData) {
      const datasets = stackedData.rows.map((row, index) => ({
        label: row.title,
        data: row.values,
        backgroundColor: COLORS[index % COLORS.length],
        borderWidth: 0,
        borderRadius: 3,
      }));
      chartConfig = {
        type: 'bar',
        data: { labels, datasets },
        options: {
          responsive: false,
          animation: { duration: 0 },
          layout: { padding: { top: 8, right: 16, bottom: 8, left: 8 } },
          plugins: {
            title: {
              display: !!chartTitle,
              text: chartTitle,
              font: { size: 13, family: 'Calibri', weight: 'bold' },
              color: '#222',
              padding: { bottom: 10 }
            },
            legend: { display: true, position: 'bottom', labels: { boxWidth: 12, font: { size: 10 } } },
            tooltip: { mode: 'index', intersect: false }
          },
          scales: {
            x: { ...cleanScaleX, stacked: true },
            y: cleanScaleY(true)
          }
        }
      };
    } else {
      const isBar = chartType === 'bar';
      chartConfig = {
        type: chartType,
        data: {
          labels,
          datasets: [{
            label: 'Count',
            data: dataValues,
            backgroundColor: COLORS,
            borderWidth: 0,
            borderRadius: isBar ? 3 : 0,
          }]
        },
        options: {
          responsive: false,
          animation: { duration: 0 },
          layout: { padding: { top: 8, right: isBar ? 16 : 8, bottom: 8, left: 8 } },
          plugins: {
            title: {
              display: !!chartTitle,
              text: chartTitle,
              font: { size: 13, family: 'Calibri', weight: 'bold' },
              color: '#222',
              padding: { bottom: 10 }
            },
            legend: {
              display: chartType === 'pie',
              position: 'right',
              labels: { boxWidth: 11, font: { size: 10, family: 'Calibri' }, padding: 10 }
            }
          },
          scales: isBar ? { x: cleanScaleX, y: cleanScaleY(false) } : undefined
        }
      };
    }

    const chartInstance = new Chart(ctx, chartConfig);

    setTimeout(() => {
      const offscreen = document.createElement('canvas');
      offscreen.width = canvas.width;
      offscreen.height = canvas.height;
      const offCtx = offscreen.getContext('2d');
      offCtx.fillStyle = '#FFFFFF';
      offCtx.fillRect(0, 0, offscreen.width, offscreen.height);
      offCtx.drawImage(canvas, 0, 0);
      const base64 = offscreen.toDataURL('image/png');
      chartInstance.destroy();
      document.body.removeChild(canvas);
      resolve(base64);
    }, 100);
  });
};

// ─── Style Helpers ────────────────────────────────────────────────────────────
const applyHeaderStyle = (row) => {
  row.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFC000' } };
    cell.font = { bold: true, size: 11, name: 'Calibri' };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.border = {
      top: { style: 'thin', color: { argb: 'FF000000' } },
      left: { style: 'thin', color: { argb: 'FF000000' } },
      bottom: { style: 'thin', color: { argb: 'FF000000' } },
      right: { style: 'thin', color: { argb: 'FF000000' } },
    };
  });
};

const applyDataStyle = (row) => {
  row.eachCell((cell) => {
    cell.font = { size: 10, name: 'Calibri' };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.border = {
      top: { style: 'thin', color: { argb: 'FFD0D0D0' } },
      left: { style: 'thin', color: { argb: 'FFD0D0D0' } },
      bottom: { style: 'thin', color: { argb: 'FFD0D0D0' } },
      right: { style: 'thin', color: { argb: 'FFD0D0D0' } },
    };
  });
};

// Alternating row zebra style for 2nd sheet data rows
const applyZebraStyle = (row, isOdd) => {
  row.eachCell((cell) => {
    cell.fill = {
      type: 'pattern', pattern: 'solid',
      fgColor: { argb: isOdd ? 'FFFFFFFF' : 'FFF5F8FF' },
    };
    cell.font = { size: 10, name: 'Calibri', color: { argb: 'FF1A1A2E' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.border = {
      top: { style: 'thin', color: { argb: 'FFE2E8F0' } },
      left: { style: 'thin', color: { argb: 'FFE2E8F0' } },
      bottom: { style: 'thin', color: { argb: 'FFE2E8F0' } },
      right: { style: 'thin', color: { argb: 'FFE2E8F0' } },
    };
  });
};

const applyAnalyticsHeaderStyle = (cell) => {
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFC000' } };
  cell.font = { bold: true, color: { argb: 'FF000000' }, size: 11, name: 'Calibri' };
  cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
  cell.border = {
    top: { style: 'thin', color: { argb: 'FFCBD5E1' } },
    left: { style: 'thin', color: { argb: 'FFCBD5E1' } },
    bottom: { style: 'thin', color: { argb: 'FFCBD5E1' } },
    right: { style: 'thin', color: { argb: 'FFCBD5E1' } },
  };
};

const autoSizeColumns = (worksheet, minWidth = 12, maxWidth = 50) => {
  worksheet.columns.forEach((column) => {
    let longest = 0;
    column.eachCell({ includeEmpty: true }, (cell) => {
      const text = cell.value ? String(cell.value) : '';
      longest = Math.max(longest, text.length);
      cell.alignment = { wrapText: true, vertical: 'middle', horizontal: 'center' };
      const width = column.width || 20;
      const lines = Math.ceil(text.length / width);
      const row = worksheet.getRow(cell.row);
      row.height = Math.max(row.height || 20, lines * 16);
    });
    column.width = Math.min(Math.max(longest + 4, minWidth), maxWidth);
  });
};

// Fill sheet background white
const fillSheetWhite = (sheet, maxRows = 500, maxCols = 25) => {
  for (let r = 1; r <= maxRows; r++) {
    const row = sheet.getRow(r);
    row.height = 15;
    for (let c = 1; c <= maxCols; c++) {
      row.getCell(c).fill = {
        type: 'pattern', pattern: 'solid',
        fgColor: { argb: 'FFFFFFFF' },
      };
    }
  }
};

// ─── 1. Survey Report Sheet ───────────────────────────────────────────────────
export const downloadSurveyReportExcel = async (rows = [], fileName = 'Survey_Report.xlsx') => {
  if (!Array.isArray(rows) || rows.length === 0) {
    toastWarn('No data available to download');
    return false;
  }

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Survey Report');

  // ── Freeze header row so it stays fixed while scrolling ──
  worksheet.views = [{ state: 'frozen', xSplit: 0, ySplit: 1, topLeftCell: 'A2' }];

  worksheet.columns = [
    { header: 'S.NO',               key: 'serialNo',         width: 7  },
    { header: 'SURVEY NAME',        key: 'survey_name',      width: 32 },
    { header: 'NO OF QUESTIONS',    key: 'no_of_questions',  width: 16 },
    { header: 'PERIOD',             key: 'period',           width: 16 },
    { header: 'START DATE',         key: 'start_date',       width: 14 },
    { header: 'END DATE',           key: 'end_date',         width: 14 },
    { header: 'TOTAL ASSIGNED',     key: 'totalAssigned',    width: 15 },
    { header: 'TOTAL ATTENDED',     key: 'totalAttended',    width: 15 },
    { header: 'TOTAL NOT ATTENDED', key: 'totalNotAttended', width: 18 },
  ];

  applyHeaderStyle(worksheet.getRow(1));
  worksheet.getRow(1).height = 30;

  rows.forEach((row, idx) => {
    const addedRow = worksheet.addRow({
      serialNo:         row.serialNo         ?? '--',
      survey_name:      row.survey_name      ?? '--',
      no_of_questions:  row.no_of_questions  ?? '--',
      period:           row.period           ?? '--',
      start_date:       row.start_date       ?? '--',
      end_date:         row.end_date         ?? '--',
      totalAssigned:    row.totalAssigned    ?? '--',
      totalAttended:    row.totalAttended    ?? '--',
      totalNotAttended: row.totalNotAttended ?? '--',
    });
    applyDataStyle(addedRow);
    // Zebra stripe
    addedRow.eachCell((cell) => {
      cell.fill = {
        type: 'pattern', pattern: 'solid',
        fgColor: { argb: idx % 2 === 0 ? 'FFFFFFFF' : 'FFFFF9E6' },
      };
    });
    addedRow.height = 20;
  });

  autoSizeColumns(worksheet, 10, 35);

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), fileName);
  return true;
};

// ─── 2. Survey Users Sheet ────────────────────────────────────────────────────
export const downloadSurveyUsersExcel = async (
  rows = [], dynamicCols = [],
  fileName = 'Survey_Users.xlsx',
  analyticsData = []
) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    toastWarn('No data available to download');
    return false;
  }

  const workbook = new ExcelJS.Workbook();

  const defaultCols = [
    { header: 'S.NO',          key: 'serialNo',     width: 7  },
    { header: 'EMPLOYEE CODE', key: 'employeeCode', width: 18 },
    { header: 'USER NAME',     key: 'userName',     width: 24 },
  ];

  const excelCols = dynamicCols
    .filter(col => !['serialNo','userName','action','employeeCode','checkbox'].includes(col.field))
    .map(col => ({
      header: col.headerName,
      key: col.field,
      width: Math.min(Math.max((col.headerName || '').length + 4, 16), 40),
    }));

  const allCols = [...defaultCols, ...excelCols];

  // Group rows by employee code prefix
  const groups = {};
  rows.forEach((row) => {
    const empCode = String(row.employeeCode || '').trim();
    const uName   = String(row.userName     || '').trim();
    if (!empCode || empCode === '--' || empCode.toLowerCase() === 'undefined' ||
        !uName   || uName   === '--' || uName.toLowerCase()   === 'undefined') return;

    const rowData = { ...row };
    excelCols.forEach(col => { if (!rowData[col.key]) rowData[col.key] = '--'; });

    const match  = empCode.match(/^[a-zA-Z]+/);
    const prefix = match ? match[0].toUpperCase() : 'OTHER';
    if (!groups[prefix]) groups[prefix] = [];
    groups[prefix].push(rowData);
  });

  if (Object.keys(groups).length === 0) {
    toastWarn('No downloadable user records with valid names and codes.');
    return false;
  }

  const prefixOrder = ['OWS', 'QCI'];
  const sortedPrefixes = Object.keys(groups).sort((a, b) => {
    const iA = prefixOrder.indexOf(a), iB = prefixOrder.indexOf(b);
    if (iA !== -1 && iB !== -1) return iA - iB;
    if (iA !== -1) return -1; if (iB !== -1) return 1;
    if (a === 'OTHER') return 1; if (b === 'OTHER') return -1;
    return a.localeCompare(b);
  });

  sortedPrefixes.forEach(prefix => {
    const sheetName = prefix.length <= 25 ? `${prefix} Users` : prefix.substring(0, 25);
    const sheet = workbook.addWorksheet(sheetName);

    // ── Freeze header row so it stays fixed while scrolling ──
    sheet.views = [{ state: 'frozen', xSplit: 0, ySplit: 1, topLeftCell: 'A2', zoomScale: 85 }];
    sheet.columns = allCols;

    applyHeaderStyle(sheet.getRow(1));
    sheet.getRow(1).height = 30;

    let sno = 1;
    groups[prefix].forEach((rowData, idx) => {
      rowData.serialNo = sno++;
      const addedRow = sheet.addRow(rowData);
      addedRow.height = 20;
      applyZebraStyle(addedRow, idx % 2 === 0);
    });

    autoSizeColumns(sheet, 10, 40);
  });

  // ── Analytics / 3rd Sheet ────────────────────────────────────────────────
  let categoriesData = [];
  if (analyticsData && !Array.isArray(analyticsData) && Array.isArray(analyticsData.categories)) {
    categoriesData = analyticsData.categories;
  } else if (Array.isArray(analyticsData)) {
    categoriesData = analyticsData;
  }

  if (categoriesData.length > 0) {
    const parseQuestionData = (q) => {
      const qTypeRaw = String(q.question_type || '').toLowerCase();
      let cols = [], rows = [], isMatrix = false, isText = false, matrixSubQuestions = [];

      if (qTypeRaw === 'matrix' && q.matrix_analysis) {
        isMatrix = true;
        if (q.matrix_analysis.length > 0) {
          cols = q.matrix_analysis[0].options.map(o => o.option);
          matrixSubQuestions = q.matrix_analysis.map(r => ({ title: r.row, values: r.options.map(o => o.count) }));
          rows = matrixSubQuestions;
        }
      } else if (qTypeRaw === 'rating' && q.ratings) {
        cols = q.ratings.map(r => `${r.rating} Star`);
        rows = [{ title: q.question, values: q.ratings.map(r => r.count) }];
      } else if ((qTypeRaw === 'radio' || qTypeRaw === 'checkbox' || qTypeRaw === 'multichoice') && q.options) {
        cols = q.options.map(o => o.option);
        rows = [{ title: q.question, values: q.options.map(o => o.count) }];
      } else if ((qTypeRaw === 'sliders' || qTypeRaw === 'slider') && q.slider_ranges) {
        cols = q.slider_ranges.map(s => s.range);
        rows = [{ title: q.question, values: q.slider_ranges.map(s => s.count) }];
      } else if ((qTypeRaw === 'text' || qTypeRaw === 'freetext') && q.answers) {
        isText = true;
        cols = ['Answers'];
        rows = q.answers.map((ans, idx) => ({ title: idx === 0 ? q.question : '', values: [ans] }));
      } else {
        cols = q.columns || ['Poor', 'Average', 'Good', 'Excellent'];
        const fallbackRows = q.rows || [];
        rows = fallbackRows.map(r => ({
          title: r.label || r.option || r.title || q.question,
          values: cols.map((c, i) => (r.counts || r.values || [])[i] !== undefined ? (r.counts || r.values || [])[i] : (r[c] || 0))
        }));
      }
      return { cols, rows, mainTitle: q.question, isMatrix, isText, matrixSubQuestions, questionType: qTypeRaw };
    };

    const allChartItems = [];
    const sheet = workbook.addWorksheet('Analytics');

    // Freeze header row in analytics sheet too
    sheet.views = [{ state: 'frozen', xSplit: 0, ySplit: 1, topLeftCell: 'A2', zoomScale: 85 }];

    let currentRow = 1;

    categoriesData.forEach((group) => {
      const catName   = group.category_name || group.category || 'Analytics';
      const questions = group.questions || group.responses || [];
      const parsedQuestions = questions.map(parseQuestionData);

      const groupsByCols = [];
      parsedQuestions.forEach(pq => {
        const colStr = pq.isText ? 'TEXT_ANSWERS' : pq.cols.join('|||');
        let g = groupsByCols.find(x => x.colStr === colStr);
        if (!g) { g = { colStr, cols: pq.isText ? ['Answers'] : pq.cols, questions: [] }; groupsByCols.push(g); }
        g.questions.push(pq);
      });

      groupsByCols.forEach((g, gIndex) => {
        const maxCols = g.cols.length;
        const questionTypes = [...new Set(g.questions.map(q => {
          const t = String(q.questionType || '').toLowerCase();
          if (t === 'matrix') return 'Matrix';
          if (t === 'rating') return 'Rating';
          if (t === 'checkbox' || t === 'multichoice') return 'Checkbox';
          if (t === 'sliders' || t === 'slider') return 'Slider';
          if (t === 'text' || t === 'freetext') return 'Text';
          if (t === 'radio') return 'Radio';
          // Fallback guessing
          if (q.isMatrix) return 'Matrix';
          if (q.isText)   return 'Text';
          if (q.cols?.some(col => String(col).toLowerCase().includes('star'))) return 'Rating';
          if (q.cols?.every(col => String(col).includes('-'))) return 'Slider';
          return 'Radio';
        }))].join(', ');

        const groupTitle = groupsByCols.length > 1
          ? `${catName} ${gIndex + 1} (${questionTypes})`
          : `${catName} (${questionTypes})`;

        // Category header band
        const catRow  = sheet.getRow(currentRow);
        catRow.height = 28;
        const catCell = catRow.getCell(1);
        catCell.value = groupTitle;
        catCell.fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F3864' } };
        catCell.font  = { bold: true, color: { argb: 'FFFFFFFF' }, size: 12, name: 'Calibri' };
        catCell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
        sheet.mergeCells(currentRow, 1, currentRow, maxCols + 1);
        currentRow++;

        // Column header row
        const hRow  = sheet.getRow(currentRow);
        hRow.height = 24;
        const hCell = hRow.getCell(1);
        hCell.value = 'Question / Options';
        applyAnalyticsHeaderStyle(hCell);
        g.cols.forEach((colName, cIdx) => {
          const cCell = hRow.getCell(cIdx + 2);
          cCell.value = colName;
          applyAnalyticsHeaderStyle(cCell);
        });
        currentRow++;

        let columnTotals = new Array(g.cols.length).fill(0);
        let dataRowIdx   = 0;

        g.questions.forEach(pq => {
          if (pq.isMatrix) {
            // Matrix main title row
            const mRow  = sheet.getRow(currentRow);
            mRow.height = 24;
            const mCell = mRow.getCell(1);
            mCell.value = pq.mainTitle;
            mCell.fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8F0FE' } };
            mCell.font  = { bold: true, size: 10, italic: true, name: 'Calibri', color: { argb: 'FF1A1A2E' } };
            mCell.alignment = { vertical: 'middle', indent: 1 };
            sheet.mergeCells(currentRow, 1, currentRow, maxCols + 1);
            currentRow++;
          }

          pq.rows.forEach(r => {
            const dRow     = sheet.getRow(currentRow);
            dRow.height    = 20;
            const titleCell = dRow.getCell(1);
            titleCell.value = r.title || pq.mainTitle || '--';
            titleCell.fill  = {
              type: 'pattern', pattern: 'solid',
              fgColor: { argb: dataRowIdx % 2 === 0 ? 'FFFFFFFF' : 'FFF5F8FF' },
            };
            titleCell.font      = { size: 10, name: 'Calibri' };
            titleCell.alignment = { wrapText: true, vertical: 'middle', indent: 1 };
            titleCell.border    = {
              top: { style: 'thin', color: { argb: 'FFE2E8F0' } },
              left: { style: 'thin', color: { argb: 'FFE2E8F0' } },
              bottom: { style: 'thin', color: { argb: 'FFE2E8F0' } },
              right: { style: 'thin', color: { argb: 'FFE2E8F0' } },
            };

            pq.cols.forEach((colName, cIdx) => {
              const vCell  = dRow.getCell(cIdx + 2);
              const val    = r.values[cIdx] !== undefined ? r.values[cIdx] : '';
              vCell.value  = val;
              vCell.fill   = {
                type: 'pattern', pattern: 'solid',
                fgColor: { argb: dataRowIdx % 2 === 0 ? 'FFFFFFFF' : 'FFF5F8FF' },
              };
              vCell.font      = { size: 10, name: 'Calibri' };
              vCell.alignment = { horizontal: 'center', vertical: 'middle' };
              vCell.border    = {
                top:    { style: 'thin', color: { argb: 'FFE2E8F0' } },
                left:   { style: 'thin', color: { argb: 'FFE2E8F0' } },
                bottom: { style: 'thin', color: { argb: 'FFE2E8F0' } },
                right:  { style: 'thin', color: { argb: 'FFE2E8F0' } },
              };
              if (!isNaN(val) && val !== '') columnTotals[cIdx] += Number(val);
            });
            dataRowIdx++;
            currentRow++;
          });

          // Chart data collection
          if (!pq.isText) {
            const optionKey = pq.cols.join('|||');
            if (pq.isMatrix) {
              // Matrix → stacked bar chart (same size as regular charts)
              let stackedChart = allChartItems.find(x => x.optionKey === `${optionKey}_stacked`);
              if (!stackedChart) {
                allChartItems.push({
                  optionKey: `${optionKey}_stacked`,
                  labels: pq.cols,
                  category: catName,
                  questionType: `Matrix: ${pq.mainTitle}`,
                  questions: [pq.mainTitle],
                  isStacked: true,
                  stackedData: { rows: pq.matrixSubQuestions || pq.rows }
                });
              }
            } else {
              let qType = 'Radio';
              const t = String(pq.questionType || '').toLowerCase();
              if (t === 'rating') {
                qType = 'Rating';
              } else if (t === 'sliders' || t === 'slider') {
                qType = 'Slider';
              } else if (t === 'checkbox' || t === 'multichoice') {
                qType = 'Checkbox';
              } else if (t === 'text' || t === 'freetext') {
                qType = 'Text';
              } else if (t === 'radio') {
                qType = 'Radio';
              } else {
                // Fallback to old guessing logic
                if (pq.cols?.some(col => String(col).toLowerCase().includes('star'))) qType = 'Rating';
                else if (pq.cols?.every(col => String(col).includes('-'))) qType = 'Slider';
                else if (pq.rows?.[0]?.values?.length > 4) qType = 'Checkbox';
              }

              let existing = allChartItems.find(x => x.optionKey === optionKey && !x.isStacked);
              if (!existing) {
                existing = {
                  optionKey, labels: pq.cols,
                  data: new Array(pq.cols.length).fill(0),
                  category: catName, questionType: qType, questions: [], isStacked: false
                };
                allChartItems.push(existing);
              }
              existing.questions.push(pq.mainTitle);
              pq.rows.forEach(r => r.values.forEach((v, idx) => { existing.data[idx] += Number(v || 0); }));
            }
          }
        });

        // SUB TOTAL row
        if (g.colStr !== 'TEXT_ANSWERS') {
          const tRow  = sheet.getRow(currentRow);
          tRow.height = 24;
          const tCell = tRow.getCell(1);
          tCell.value = 'SUB TOTAL';
          tCell.fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDCE6F1' } };
          tCell.font  = { bold: true, size: 10, name: 'Calibri', color: { argb: 'FF1F3864' } };
          tCell.alignment = { vertical: 'middle', indent: 1 };
          tCell.border    = {
            top: { style: 'medium', color: { argb: 'FF2E75B6' } },
            left: { style: 'thin', color: { argb: 'FFE2E8F0' } },
            bottom: { style: 'thin', color: { argb: 'FFE2E8F0' } },
            right: { style: 'thin', color: { argb: 'FFE2E8F0' } },
          };

          g.cols.forEach((_, cIdx) => {
            const vCell = tRow.getCell(cIdx + 2);
            vCell.value = columnTotals[cIdx];
            vCell.fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDCE6F1' } };
            vCell.font  = { bold: true, size: 10, name: 'Calibri', color: { argb: 'FF1F3864' } };
            vCell.alignment = { horizontal: 'center', vertical: 'middle' };
            vCell.border    = {
              top:    { style: 'medium', color: { argb: 'FF2E75B6' } },
              left:   { style: 'thin', color: { argb: 'FFE2E8F0' } },
              bottom: { style: 'thin', color: { argb: 'FFE2E8F0' } },
              right:  { style: 'thin', color: { argb: 'FFE2E8F0' } },
            };
          });
          currentRow++;
        }

        currentRow += 2;
      });
    });

    autoSizeColumns(sheet, 14, 50);

    // ── Graphs Sheet ─────────────────────────────────────────────────────────
    if (allChartItems.length > 0) {
      const graphSheet = workbook.addWorksheet('Graphs');
      fillSheetWhite(graphSheet, 600, 25);
      graphSheet.views = [{ zoomScale: 75 }];

      // Standard chart size — same for ALL chart types including matrix
      const CHART_W  = 420;
      const CHART_H  = 280;
      const COL_GAP  = 9;   // columns between pie and bar
      const ROW_STEP = 16;  // excel rows per chart block

      let rowIdx = 1;

      for (let i = 0; i < allChartItems.length; i++) {
        const item = allChartItems[i];
        try {
          const safeTitle = `${item.category} — ${item.questionType}`
            .replace(/\s+/g, ' ').trim().substring(0, 120);

          // Title row
          const titleRow  = graphSheet.getRow(rowIdx);
          titleRow.height = 22;
          const titleCell = titleRow.getCell(1);
          titleCell.value = safeTitle;
          titleCell.fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };
          titleCell.font  = { bold: true, size: 12, name: 'Calibri', color: { argb: 'FF1F3864' } };
          titleCell.alignment = { vertical: 'middle' };
          graphSheet.mergeCells(rowIdx, 1, rowIdx, 18);
          rowIdx++;

          if (item.isStacked && item.stackedData) {
            // Matrix stacked bar — SAME size as regular charts (not oversized)
            const stackedBase64 = await generateChartImage(
              item.labels, null, safeTitle,
              'stackedBar', item.stackedData,
              CHART_W, CHART_H        // ← same dimensions as pie/bar
            );
            const imgId = workbook.addImage({ base64: stackedBase64, extension: 'png' });
            graphSheet.addImage(imgId, {
              tl: { col: 0, row: rowIdx },
              ext: { width: CHART_W, height: CHART_H }
            });
            rowIdx += ROW_STEP;

          } else {
            // Pie chart (left side)
            const pieBase64 = await generateChartImage(
              item.labels, item.data, safeTitle, 'pie',
              null, CHART_W, CHART_H
            );
            const pieId = workbook.addImage({ base64: pieBase64, extension: 'png' });
            graphSheet.addImage(pieId, {
              tl: { col: 0, row: rowIdx },
              ext: { width: CHART_W, height: CHART_H }
            });

            // Bar chart (right side)
            const barBase64 = await generateChartImage(
              item.labels, item.data, safeTitle, 'bar',
              null, CHART_W, CHART_H
            );
            const barId = workbook.addImage({ base64: barBase64, extension: 'png' });
            graphSheet.addImage(barId, {
              tl: { col: COL_GAP, row: rowIdx },
              ext: { width: CHART_W, height: CHART_H }
            });

            rowIdx += ROW_STEP;
          }

          // Spacer row
          rowIdx += 1;

        } catch (err) {
          console.error('Chart generation error:', err);
        }
      }
    }
  }

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), fileName);
  return true;
};